import React, { Fragment, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom/dist";
import Table from "../../core/pagination/datatable";
import { Download, PlusCircle, Upload } from "feather-icons-react/build/IconComponents";
import { ErrorToaster } from "../components/toaster/Toaster";
import InvoiceServices from "../../services/Invoice";
import InvoiceForm from "../../core/modals/invoice/invoiceForm";
import { cleanObject, Debounce, getMonthDate } from "../../utils";
import { useSelector } from "react-redux";
import { all_routes } from "../../Router/all_routes";
import Swal from "sweetalert2";
import { deleteAlert } from "../components/sweetAlert";
import { DatePicker } from "antd";
import { Calendar, StopCircle } from "react-feather";
import dayjs from "dayjs";
import Select from "react-select";
import { invoiceStatuses } from "../../core/json/invoiceStatuses";
import ExportInvoices from "../../core/modals/invoice/invoiceExport";

const Invoices = () => {

  const routes = all_routes;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { RangePicker } = DatePicker;

  const { userData } = useSelector((state) => {
    return {
      userData: state.user_detail
    }
  });

  // *For Loader
  const [loader, setLoader] = useState(false);

  const [validationLoading, setValidationLoading] = useState(false);
  const [submittingLoading, setSubmittingLoading] = useState(false);

  // *For Invoices
  const [invoices, setInvoices] = useState([]);

  // *For Pagination
  const [totalCount, setTotalCount] = useState(0);
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const pageLimit = parseInt(searchParams.get("limit") || "10", 10);
  const searchText = searchParams.get("search") || "";
  const searchBuyer = searchParams.get("buyer") || "";
  const searchBuyerNtn = searchParams.get("buyerntn") || "";
  const status = searchParams.get("status") || "";
  const startDate = searchParams.get("from") || "";
  const endDate = searchParams.get("to") || "";

  // *For Get Invoices
  const getInvoices = async (page = currentPage, limit = pageLimit) => {
    setLoader(true)
    try {
      const params = {
        ...Object.fromEntries(searchParams.entries()),
        page,
        limit
      }
      const { data } = await InvoiceServices.getInvoices(params)
      setInvoices(data?.invoices)
      setTotalCount(data?.pagination?.total)
    } catch (error) {
      ErrorToaster(error)
    } finally {
      setLoader(false)
    }
  }

  // *For Delete Invoice
  const deleteInvoice = async (id) => {
    try {
      const isConfirmed = await deleteAlert()

      if (isConfirmed) {
        const { message } = await InvoiceServices.deleteInvoice(id);
        getInvoices();

        Swal.fire({
          title: "Deleted!",
          text: message,
          icon: "success",
          confirmButtonText: "OK",
        });
      } else {
        Swal.close();
      }
    } catch (error) {
      ErrorToaster(error)
    }
  }

  // *For Validate Invoice
  const validateInvoice = async (id) => {
    setValidationLoading(true)
    try {
      const obj = {
        invoice_id: id
      }
      const { message } = await InvoiceServices.validateInvoice(obj)
      // SuccessToaster(message)
      Swal.fire({
        title: "Validated!",
        text: message,
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      ErrorToaster(error)
    } finally {
      getInvoices()
      setValidationLoading(false)
    }
  }

  // *For Submit Invoice
  const submitInvoice = async (id) => {
    setSubmittingLoading(true)
    try {
      const obj = {
        invoice_id: id
      }
      const { message } = await InvoiceServices.submitInvoice(obj)
      // SuccessToaster(message)
      Swal.fire({
        title: "Submitted!",
        text: message,
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      ErrorToaster(error)
    } finally {
      getInvoices()
      setSubmittingLoading(false)
    }
  }

  // *For Handle Pagination and Filters
  const handlePaginationFilter = (data) => {
    try {
      const obj = {
        ...data,
      }
      const updatedParams = {
        ...Object.fromEntries(searchParams.entries()),
        page: 1,
        ...cleanObject(obj)
      };
      Debounce(() => setSearchParams(updatedParams));
    } catch (error) {
      console.log(' handlePaginationFilter => error:', error)
    }
  }

  const columns = [
    {
      title: "Invoice Ref",
      dataIndex: "invoice_ref_no",
      render: (text, record) => (
        <span>
          {text || '-'}
        </span>
      ),
    },
    {
      title: "Invoice Date",
      dataIndex: "invoice_date",
      render: (text) => (
        <span>
          {getMonthDate(text)}
        </span>
      ),
    },
    {
      title: "Invoice Type",
      dataIndex: "invoice_type",
      render: (text) => (
        <span>
          {text || '-'}
        </span>
      ),
    },
    {
      title: "Buyer Name",
      dataIndex: "buyer_business_name",
      render: (text) => (
        <span>
          {text || '-'}
        </span>
      ),
    },
    {
      title: "Buyer NTN/CNIC",
      dataIndex: "buyer_ntn_cnic",
      render: (text) => (
        <span>
          {text || '-'}
        </span>
      ),
    },
    // {
    //   title: "Buyer Province",
    //   dataIndex: "buyer_province",
    //   render: (text) => (
    //     <span>
    //       {text || '-'}
    //     </span>
    //   ),
    // },
    // {
    //   title: "Buyer Address",
    //   dataIndex: "buyer_address",
    //   render: (text) => (
    //     <div style={{ width: '200px', whiteSpace: 'break-spaces' }}>
    //       {text || '-'}
    //     </div>
    //   ),
    // },
    // {
    //   title: "Seller Name",
    //   dataIndex: "seller_business_name",
    //   render: (text) => (
    //     <span>
    //       {text || '-'}
    //     </span>
    //   ),
    // },
    // {
    //   title: "Seller NTN/CNIC",
    //   dataIndex: "seller_ntn_cnic",
    //   render: (text) => (
    //     <span>
    //       {text || '-'}
    //     </span>
    //   ),
    // },
    // {
    //   title: "Seller Province",
    //   dataIndex: "seller_province",
    //   render: (text) => (
    //     <span>
    //       {text || '-'}
    //     </span>
    //   ),
    // },
    // {
    //   title: "Seller Address",
    //   dataIndex: "seller_address",
    //   render: (text) => (
    //     <div style={{ width: '200px', whiteSpace: 'break-spaces' }}>
    //       {text || '-'}
    //     </div>
    //   ),
    // },
    {
      title: "Status",
      dataIndex: "status",
      render: (text) => {
        const statusColor = text === "PENDING" ? "badge-linegray" : text === "SUBMITTED" ? "badge-linesuccess" : text === "VALID" ? "badge-linewarning" : "badge-linedanger"
        return (
          <span
            className={`badge ${statusColor} text-capitalize`}
          >
            {text}
          </span>
        )
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      render: (text) => (
        <div style={{ width: '250px', whiteSpace: 'break-spaces' }}>
          {text || '-'}
        </div>
      ),
    },
    {
      title: "FBR Invoice No",
      dataIndex: "fbr_invoice_number",
      render: (text) => (
        <div>
          {text ? `FBR # ${text}` : '-'}
        </div>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text, record) => (
        <div className="action-table-data">
          {/* {record?.fbr_invoice_number && */}
          <button type="button" className="btn btn-sm btn-primary btn-wave waves-effect waves-light me-2"
            // disabled={!record?.fbr_invoice_number}
            onClick={() => navigate(`${routes?.viewInvoice}/${record?.id}`)}
          >
            View Invoice
          </button>
          {/* } */}
          {!record?.submitted_at &&
            <>
              {record?.status !== 'SUBMITTED' && record?.status !== 'VALID' &&
                <button type="button" className="btn btn-sm btn-warning btn-wave waves-effect waves-light me-2"
                  disabled={validationLoading}
                  onClick={() => validateInvoice(record?.id)}
                >
                  Validate
                </button>
              }
              {record?.status === 'VALID' &&
                <button type="button" className="btn btn-sm btn-success btn-wave waves-effect waves-light me-2"
                  disabled={submittingLoading}
                  onClick={() => submitInvoice(record?.id)}
                >
                  Submit
                </button>
              }
              {record?.status !== 'SUBMITTED' &&
                <button type="button" className="btn btn-sm btn-info btn-wave waves-effect waves-light me-2"
                  disabled={record?.status === 'SUBMITTED'}
                  onClick={() => navigate(`${routes?.editInvoice}/${record?.id}`)}
                >
                  Edit
                </button>
              }
              {record?.status !== 'SUBMITTED' &&
                <button type="button" className="btn btn-sm btn-danger btn-wave waves-effect waves-light me-2"
                  disabled={record?.status === 'SUBMITTED'}
                  onClick={() => deleteInvoice(record?.id)}
                >
                  Delete
                </button>
              }
            </>
          }
        </div>
      ),
    },
  ];

  useEffect(() => {
    getInvoices()
  }, [searchParams]);

  return (
    <Fragment>

      <InvoiceForm getData={() => getInvoices()} />

      <ExportInvoices />

      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Invoice List</h4>
                <h6>Manage your invoices</h6>
              </div>
            </div>
            {userData?.role === 'user' &&
              <Fragment>
                <div className="page-btn">
                  <Link
                    to={routes.addInvoice}
                    className="btn btn-added"
                  >
                    <PlusCircle className="me-2" />
                    Add Invoice
                  </Link>
                </div>
                <div className="page-btn">
                  <Link
                    to="#"
                    className="btn btn-added btn-warning"
                    data-bs-toggle="modal"
                    data-bs-target="#open-modal"
                  >
                    <Upload className="me-2" />
                    Import Invoices
                  </Link>
                </div>
                <div className="page-btn">
                  <Link
                    to="#"
                    className="btn btn-added color"
                    data-bs-toggle="modal"
                    data-bs-target="#export-modal"
                  >
                    <Download className="me-2" />
                    Export Invoices
                  </Link>
                </div>
              </Fragment>
            }
          </div>
          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-top" style={{ display: 'block' }}>
                <div className="row">
                  <div className="col-lg-3 col-sm-4 col-6">
                    <div className="search-set">
                      <div className="search-input">
                        <input
                          type="text"
                          defaultValue={searchText}
                          placeholder="Search by invoice ref"
                          className="form-control form-control-sm formsearch"
                          style={{ width: '230px' }}
                          onChange={(e) =>
                            handlePaginationFilter({ search: e.target.value })
                          }
                        />
                        <Link to className="btn btn-searchset">
                          <i data-feather="search" className="feather-search" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-2 col-sm-3 col-6">
                    <div className="search-set">
                      <div className="search-input">
                        <input
                          type="text"
                          defaultValue={searchBuyer}
                          placeholder="Search by buyer"
                          className="form-control form-control-sm formsearch"
                          style={{ width: '150px' }}
                          onChange={(e) =>
                            handlePaginationFilter({ buyer: e.target.value })
                          }
                        />
                        <Link to className="btn btn-searchset">
                          <i data-feather="search" className="feather-search" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-2 col-sm-3 col-6">
                    <div className="search-set">
                      <div className="search-input">
                        <input
                          type="text"
                          defaultValue={searchBuyerNtn}
                          placeholder="Search by ntn/cnic"
                          className="form-control form-control-sm formsearch"
                          style={{ width: '150px' }}
                          onChange={(e) =>
                            handlePaginationFilter({ buyerntn: e.target.value })
                          }
                        />
                        <Link to className="btn btn-searchset">
                          <i data-feather="search" className="feather-search" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-2 col-sm-3 col-6">
                    <div className="input-blocks mb-0">
                      <StopCircle className="info-img" />
                      <Select
                        classNamePrefix="react-select"
                        className="img-select"
                        options={invoiceStatuses}
                        placeholder="Status"
                        value={invoiceStatuses.find((item) => item.value === status)}
                        onChange={(e) => handlePaginationFilter({ status: e.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <div className="input-blocks mb-0">
                      <div className="input-groupicon calender-input">
                        <Calendar className="info-img" />
                        <RangePicker
                          value={startDate && [dayjs(startDate), dayjs(endDate)]}
                          onChange={(date) => handlePaginationFilter({ from: date ? dayjs(date[0]).format('YYYY-MM-DD') : '', to: date ? dayjs(date[1]).format('YYYY-MM-DD') : '' })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <Table
                  loading={loader}
                  columns={columns}
                  dataSource={invoices}
                  totalCount={totalCount}
                  pageSize={pageLimit}
                  currentPage={currentPage}
                  onChange={(page, pageSize) => handlePaginationFilter({ page: page, limit: pageSize })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Invoices;
